<template>
    <div class="area container-fluid">
        <div class="row mt-5 mb-2">
            <div class="col-6">
                <header-message class="mt-3 mb-3">
                    Yaw &amp; Roll Values: +/- 1 &deg;<br/>
                    Use Auggie Cover
                </header-message>
                <header-message v-if="standardMarker" class="mt-2">
                    Use Standard Marker<br/>
                    <img src="/img/MarkerStandardSmall.png" class="marker img-fluid mt-3" alt="Standard Marker">
                </header-message>
                <header-message v-if="highAngleMarker" class="mt-2">
                    Use High-Angle Marker<br/>
                    <img src="/img/MarkerHighAngleSmall.png" class="marker img-fluid mt-3" alt="High-Angle Marker">
                </header-message>
                <header-message v-if="highAccuracyMarker" class="mt-2">
                    Use High-Accuracy Marker<br/>
                    <img src="/img/MarkerHighAccuracySmall.png" class="marker img-fluid mt-3" alt="High-Accuracy Marker">
                </header-message>
                <header-message v-if="usesExternalGyro" class="mt-2">
                    Place the external gyro sensor flat on top of the Auggie unit.
                </header-message>
            </div>
            <div class="leglengthdiv col-6 mt-3 mb-4">
                <div class="leglengtharea">
                    <div class="lengtha">Leg Position <b>{{ legA() }}</b></div>
                    <div class="lengthb">Leg Position <b>{{ legB() }}</b></div>
                    <div class="lengthc">Leg Position <b>{{ legC() }}</b></div>
                </div>
            </div>
        </div>
        <div class="buttonrow">
            <button-button type="button" class="btn btn-secondary me-4" @click.prevent="cancelClicked" :disabled="loading">Cancel</button-button>
            <spin-button class="btn-primary" type="submit" @click.prevent="nextClicked" :spinning="loading || IsControlled" :disabled="loading || IsControlled">Ready For Alignment</spin-button>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .status {
        padding: 0 0.2em;
        font-size: 1.8rem;
    }
    .marker {
        height:20vh;
    }
    .leglengtharea {
        position: relative;
        background-image: url(/img/LegLengths.png);
        background-size: cover;
        width: 40vw;
        height: 40vh;
    }
    .lengtha, .lengthb, .lengthc {
        position: absolute;
        font-size: 1.8rem;
        text-shadow: 0 0 5px #000, 0 0 25px #000, 0 0 35px #000, 0 0 50px #000, 0 0 5px #000, 0 0 25px #000, 0 0 35px #000, 0 0 50px #000;
    }
    .lengtha, .lengthb {
        text-align: center;
        line-height: 1.2em;
    }
    .lengtha {
        left:2rem;
        top:0px;
    }
    .lengthb {
        right:2rem;
        top:0px;
    }
    .lengthc {
        bottom:.5rem;
        left:200px;
    }

    @media screen and (max-width: 1919px) {
        .lengtha,
        .lengthb {
            width:120px;
        }
    }
</style>

<script lang="ts">

import { defineComponent } from "vue";

import PageBase from "@/shared/components/common/PageBase";
import HeaderMessage from "@/components/HeaderMessage.vue";

import { ButtonClicked } from "@/shared/enums/ButtonClicked";
import { confirmOk, confirmYesNo } from "@/shared/components/common/AlertDialog.vue";
import { CalibrationEventSupport } from "@/support/CalibrationEvent";
import { CalibrationEvent } from "@/shared/enums/CalibrationEvent";
import { Global } from "@/support/GlobalData";
import { SignalR } from "@/support/SignalR";
import { DeviceStatusDto } from "@/shared/models/DeviceStatusDto";
import { MarkerType } from "@/shared/enums/MarkerType";
import { AUGGIEAVAILABLE } from "@/main";

export default defineComponent({
    name: "CalibrationCameraSetup",
    mixins: [PageBase],
    components: {
        HeaderMessage,
    },
    computed: {
        standardMarker(): boolean {
            return Global.Marker === MarkerType.Standard;
        },
        highAngleMarker(): boolean {
            return Global.Marker === MarkerType.HighAngle;
        },
        highAccuracyMarker(): boolean {
            return Global.Marker === MarkerType.HighAccuracy;
        },
        IsControlled(): boolean {
            return Global.IsTechControlled;
        },
        usesExternalGyro(): boolean {
            return AUGGIEAVAILABLE && Global.HasDualGyro && Global.UseGyroYPRInd;
        }
    },
    methods: {
        legA(): number {
            return Global.LegLengthA;
        },
        legB(): number {
            return Global.LegLengthB;
        },
        legC(): number {
            return Global.LegLengthC;
        },
        cancelClicked(): void {
            confirmYesNo("Are you sure you want to cancel this calibration?")
                .then((clicked: ButtonClicked): void => {
                    if (clicked === ButtonClicked.Ok) {
                        CalibrationEventSupport.sendEvent(CalibrationEvent.CalibrationCustomerCanceled);
                        Global.MainApp.clearSession();
                        this.$router.push("/");
                    }
                });
        },
        nextClicked(): void {
            if (!Global.MainApp.haveSession()) {
                confirmOk("The calibration session information has been lost and the session has restarted.");
                Global.MainApp.clearSession();
                this.$router.push("/");
                return;
            }
            SignalR.onGetCameraAlignmentImage = null;
            if (Global.CenterlineEnabledInd && Global.IsCenterlineSupported()) {
                this.$router.push("/Calibration/CenterlineInstruction");
            } else {
                this.$router.push("/Calibration/CameraReady");
            }
        },
        getCurrentStatusForListeners(): void {
            CalibrationEventSupport.sendEventStatus({
                Page: "CalibrationCameraSetup",
            } as DeviceStatusDto);
        }
    },
    mounted(): void {
        // Re-entering camera setup starts a fresh alignment, so the centerline step should run again.
        Global.CenterlineCompleted = false;
        if (this.usesExternalGyro) {
            // Re-entering camera setup starts a fresh alignment: clear the prior
            // ground measurement and pause monitoring until the gyro is re-zeroed.
            Global.GyroMeasurementCaptured = false;
            Global.MainApp.stopGyroPolling();
        }
        SignalR.onGetCurrentStatusForListeners = this.getCurrentStatusForListeners;
        if (Global.IsTechControlled) {
            SignalR.onGetCameraAlignmentImage = (): void => { /*ignore*/ };
            setTimeout((): void => {
                this.nextClicked();
            }, 5000);
        } else {
            SignalR.onGetCameraAlignmentImage = this.nextClicked;
        }
        this.getCurrentStatusForListeners();
    },
    unmounted(): void {
        SignalR.onGetCurrentStatusForListeners = null;
        SignalR.onGetCameraAlignmentImage = null;
    }
});
</script>
